// Legacy `PreviewModal` parity — gallery-first product preview.
.modal {
  display: flex;
  flex-direction: column;
  // A FIXED height, not max-height: the three tabs hold very different amounts
  // of content (a photo gallery vs. an empty-notes line), and sizing to content
  // would make the window jump — and move the footer buttons under the pointer —
  // on every tab switch. Each panel scrolls inside this constant box instead.
  height: min(86vh, 780px);
  max-width: 900px;
  width: min(900px, calc(100vw - 32px));
}

.header {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 16px 20px 0;
}

.title-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.title {
  font-size: 18px;
}

.rating {
  align-items: center;
  display: inline-flex;
  gap: 1px;
}

.star-on {
  color: var(--color-warning);
}

.star-off {
  color: var(--color-border-strong);
}

.budget-on {
  color: var(--color-success-foreground);
}

.budget-off {
  color: var(--color-border-strong);
}

.close {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--color-muted);
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  padding: 4px;

  &:hover {
    background: var(--color-secondary);
    color: var(--color-foreground);
  }
}

.tabs {
  border-bottom: 1px solid var(--color-border, #e2e4e9);
  margin: 12px 20px 0;
}

// The only scrolling region. `min-height: 0` is required for a flex child to
// shrink below its content and actually scroll.
.body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 20px;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px 20px;
}

.footer {
  align-items: center;
  border-top: 1px solid var(--color-border, #e2e4e9);
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 14px 20px;
}

// ── Gallery ──────────────────────────────────────────────────────────────
.gallery {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;
}

.gallery-three {
  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
  }
}

.hero-btn,
.thumb-btn {
  background: var(--color-secondary);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: block;
  overflow: hidden;
  padding: 0;
  width: 100%;
}

.hero-btn {
  height: 416px;
}

.hero-img,
.thumb-img {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.thumb-col {
  display: grid;
  gap: 16px;
  grid-template-rows: 1fr 1fr;
}

.thumb-btn {
  height: 200px;
}

// Product video in place of the gallery, same height as its main photo.
.video {
  // The body scrolls; without this the column squeezes the player.
  flex-shrink: 0;
  overflow: hidden;
  height: 416px;
  border-radius: 12px;
  background: #000;
}

.video-media {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  object-fit: contain;
}

.no-image {
  align-items: center;
  background: var(--color-secondary);
  border-radius: 12px;
  color: var(--color-muted);
  display: flex;
  height: 240px;
  justify-content: center;
}

// ── Sections ─────────────────────────────────────────────────────────────
.section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-head {
  align-items: center;
  display: flex;
  gap: 8px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.copy-button {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--color-muted);
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  padding: 2px;

  &:hover {
    background: var(--color-secondary);
    color: var(--color-foreground);
  }
}

.description-html {
  color: var(--color-muted);
  font-size: 14px;
  line-height: 1.6;

  p {
    margin: 0 0 12px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  a {
    color: var(--color-primary);
    text-decoration: underline;
  }

  ul,
  ol {
    margin: 0 0 12px;
    padding-left: 20px;
  }
}

.facilities-grid {
  display: grid;
  gap: 8px 16px;
  grid-template-columns: repeat(2, 1fr);

  @media (min-width: 640px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.facility-item {
  align-items: flex-start;
  color: var(--color-muted);
  display: flex;
  font-size: 14px;
  gap: 4px;
}

.facility-icon {
  color: var(--color-primary);
  flex-shrink: 0;
}

// ── Suppliers + Created By ───────────────────────────────────────────────
.two-col {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr 1fr;
}

/* Melhoria 6 §2: the two document sources are visually distinct without needing
   a filter or a tab switch — they are stacked sections with their own headings
   and source badges. */
.document-sources {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

/* T-196: same stacked-sections treatment as the two document sources — the
   supplier's CRM information above the product's own private notes, each with
   its own heading and source badge. */
.note-sources {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.source-head {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.source-badge {
  background: var(--color-background-secondary, #f3f4f6);
  border-radius: 6px;
  color: var(--color-text-muted, #535862);
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  white-space: nowrap;
}

.source-title {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 2px;
}

// The full-screen lightbox now lives in the shared `ImageLightbox` component
// (apps/web/src/shared/ui/image-lightbox) — its styles moved there.
